import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowRight, Building2, Sparkles, Shield, Award, Target, Zap } from 'lucide-react';

const About: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation />

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative">
          <div className="max-w-6xl mx-auto px-8 pt-32 pb-16">
            <div className="text-center">
              <div className="inline-flex items-center space-x-3 px-4 py-2.5 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-8">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-slate-300">A Division of All State Title</span>
              </div>

              <h1 className="text-6xl lg:text-7xl font-black leading-tight tracking-tighter mb-8">
                <span className="block">About</span>
                <span className="block text-white">
                  Auto Access Solutions
                </span>
              </h1>

              <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
                We're revolutionizing vehicle data access with enterprise-grade solutions
                that deliver speed, accuracy, and reliability at scale.
              </p>
            </div>
          </div>
        </section>

        {/* Our Company Section */}
        <section className="relative py-16 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-black mb-4">
                  Our <span className="text-white">Company</span>
                </h2>
                <div className="inline-block px-6 py-3 bg-gradient-to-br from-gray-800/30 to-gray-700/20 rounded-xl border border-white/10">
                  <p className="text-lg font-semibold text-white">A Division of All State Title Service, Inc.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#AD0000] to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-black mb-3 text-white">Established Heritage</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Backed by the trusted reputation and proven track record of All State Title Service, Inc.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-black mb-3 text-green-400">Industry Expertise</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Leveraging decades of experience in title services to deliver cutting-edge vehicle data solutions.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-black mb-3 text-yellow-400">Speed & Accuracy</h3>
                  <p className="text-slate-400 leading-relaxed">
                    You Can Trust - our founding principle that drives everything we do.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values Grid */}
        <section className="relative py-16">
          <div className="max-w-7xl mx-auto px-8">
            <div className="grid lg:grid-cols-2 gap-8 mb-16">
              {/* Our Mission Card */}
              <div className="group bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10 hover:border-white/20 transition-all">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#AD0000] to-red-700 rounded-2xl flex items-center justify-center shadow-lg">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-black text-white">Our Mission</h2>
                </div>
                <p className="text-lg text-slate-400 leading-relaxed">
                  At Auto Access Solutions, we are dedicated to providing innovative and reliable
                  vehicle data access solutions that meet the evolving needs of our clients. Our
                  mission is to deliver exceptional service while building long-lasting relationships
                  based on trust and excellence.
                </p>
              </div>

              {/* Our Services Card */}
              <div className="group bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10 hover:border-white/20 transition-all">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">Our Services</h2>
                </div>
                <p className="text-lg text-slate-400 leading-relaxed">
                  We specialize in comprehensive vehicle data access solutions tailored to meet
                  diverse client needs. Our team of experts works closely with each client to
                  understand their unique requirements and deliver customized solutions that
                  exceed expectations.
                </p>
              </div>
            </div>

            {/* Our Values Section */}
            <div className="mb-16">
              <h2 className="text-4xl font-black mb-12 text-center">
                Our <span className="text-white">Core Values</span>
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Shield, title: 'Loyalty', description: 'We are committed to our clients\' success and build lasting partnerships', gradient: 'from-gray-600 to-gray-500' },
                  { icon: Award, title: 'Progress', description: 'We continuously improve and evolve to stay ahead of industry trends', gradient: 'from-gray-700 to-gray-600' },
                  { icon: Zap, title: 'Courage', description: 'We tackle challenges with determination and innovative thinking', gradient: 'from-[#AD0000] to-red-700' },
                  { icon: Sparkles, title: 'Innovation', description: 'We pioneer new solutions and cutting-edge technologies', gradient: 'from-gray-600 to-gray-500' }
                ].map((value, i) => (
                  <div key={i} className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all">
                    <div className={`w-12 h-12 bg-gradient-to-br ${value.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                      <value.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-black mb-3 text-white">{value.title}</h3>
                    <p className="text-slate-400 leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Section */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10">
              <h2 className="text-3xl font-black mb-12 text-center">
                Our <span className="text-white">Impact</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { value: '99.9%', label: 'API Uptime', color: 'from-white to-gray-400' },
                  { value: '500+', label: 'Enterprise Clients', color: 'from-white to-gray-300' },
                  { value: '<2s', label: 'Response Time', color: 'from-white to-gray-400' }
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className={`text-5xl font-black mb-3 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                      {stat.value}
                    </div>
                    <div className="text-slate-500 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-32">
          <div className="max-w-4xl mx-auto px-8 text-center">
            <h3 className="text-5xl font-black mb-6">Ready to Get Started?</h3>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
              Contact us today to learn more about how we can help with your vehicle data access needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button
                onClick={() => navigate('/contact')}
                className="group px-10 py-5 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 text-white font-bold text-lg rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg flex items-center space-x-3"
              >
                <span>Contact Us</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-250" />
              </button>
            </div>
            <div className="text-slate-400 space-y-2">
              <p>Email: contact@autoaccesssolutions.org</p>
              <p>Phone: 502-543-0877</p>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default About;
